import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, Plus, Download, Loader2, Layers, FileSpreadsheet, FileText } from "lucide-react";
import FlatCard from "@/components/FlatCard";
import FlatDrawer from "@/components/FlatDrawer";
import AddFlatDialog from "@/components/AddFlatDialog";

export default function Inventory() {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTower, setActiveTower] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [drawerFlat, setDrawerFlat] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editFlat, setEditFlat] = useState(null);
  const [activeFloor, setActiveFloor] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/tree");
      setTree(data);
      if (data.length && !data.find((t) => t.tower === activeTower)) {
        setActiveTower(data[0].tower);
      }
    } catch {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const currentTower = useMemo(
    () => tree.find((t) => t.tower === activeTower),
    [tree, activeTower]
  );

  const filteredFloors = useMemo(() => {
    if (!currentTower) return [];
    const s = search.trim().toLowerCase();
    return currentTower.floors
      .map((fl) => ({
        ...fl,
        units: fl.units.filter((u) => {
          const matchStatus = statusFilter === "all" || u.effective_status === statusFilter;
          const matchSearch = !s || u.flat_number.toLowerCase().includes(s) ||
            (u.owner_name || "").toLowerCase().includes(s) ||
            (u.active_tenant || "").toLowerCase().includes(s);
          return matchStatus && matchSearch;
        }),
      }))
      .filter((fl) => fl.units.length > 0);
  }, [currentTower, search, statusFilter]);

  const exportCsv = () => {
    const rows = [["Tower", "Floor", "Flat", "BHK", "Owner", "Owner Phone", "Status", "Times Rented", "Active Tenant", "Maintenance", "Parking"]];
    tree.forEach((t) => t.floors.forEach((fl) => fl.units.forEach((u) => {
      rows.push([t.tower, u.floor, u.flat_number, u.bhk_type, u.owner_name, u.owner_phone || "", u.effective_status, u.times_rented, u.active_tenant || "", u.maintenance_amount || "", u.parking_slot || ""]);
    })));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "society_flat_inventory.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Inventory exported");
  };

  const downloadReport = async (kind) => {
    const t = toast.loading(`Preparing ${kind.toUpperCase()} report…`);
    try {
      const { data } = await api.get(`/reports/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "excel" ? "society_flat_report.xlsx" : "society_flat_report.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} report downloaded`, { id: t });
    } catch {
      toast.error("Report failed", { id: t });
    }
  };

  const openFlat = (flat) => setDrawerFlat(flat.id);
  const onEditFromDrawer = (flat) => { setEditFlat(flat); setDrawerFlat(null); setAddOpen(true); };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase font-mono tracking-[0.18em] text-slate-400 mb-1">Indexed Registry</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Flat Inventory</h1>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="export-menu-button"><Download className="h-4 w-4" /> Export / Print</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => downloadReport("pdf")} data-testid="export-pdf-option">
                <FileText className="h-4 w-4 mr-2 text-rose-600" /> PDF Report (print)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadReport("excel")} data-testid="export-excel-option">
                <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportCsv} data-testid="export-csv-option">
                <Download className="h-4 w-4 mr-2 text-slate-500" /> CSV (quick)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => { setEditFlat(null); setAddOpen(true); }} className="bg-[#0F2942] gap-2" data-testid="add-flat-button"><Plus className="h-4 w-4" /> Add Flat</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search by flat no, owner or tenant…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" data-testid="inventory-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-white" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="rented">Rented Out</SelectItem>
            <SelectItem value="owner_occupied">Owner Occupied</SelectItem>
            <SelectItem value="vacant">Vacant</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 text-slate-400 gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Loading inventory…</div>
      ) : tree.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-slate-200 rounded-xl bg-white">
          <Layers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No flats yet. Add your first flat to start the registry.</p>
          <Button onClick={() => setAddOpen(true)} className="mt-4 bg-[#0F2942] gap-2" data-testid="empty-add-flat-button"><Plus className="h-4 w-4" /> Add Flat</Button>
        </div>
      ) : (
        <>
          {/* Tower tabs */}
          <div className="flex flex-wrap gap-2" data-testid="tower-tabs">
            {tree.map((t) => {
              const active = t.tower === activeTower;
              const pct = t.total ? Math.round((t.rented / t.total) * 100) : 0;
              return (
                <button
                  key={t.tower}
                  onClick={() => { setActiveTower(t.tower); setActiveFloor("all"); }}
                  data-testid={`tower-tab-${t.tower}`}
                  className={`rounded-xl border px-4 py-3 text-left transition-all ${active ? "border-[#0F2942] bg-[#0F2942] text-white shadow-md" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
                >
                  <p className="font-display font-bold">Tower {t.tower}</p>
                  <p className={`text-xs ${active ? "text-slate-300" : "text-slate-400"}`}>{t.tower_name} · {t.total} units · {pct}% rented</p>
                </button>
              );
            })}
          </div>

          {/* Floor selector */}
          {currentTower && (
            <div className="flex flex-wrap items-center gap-2" data-testid="floor-selector">
              <span className="text-[11px] uppercase font-mono tracking-[0.16em] text-slate-400 mr-1">Floor</span>
              <button
                onClick={() => setActiveFloor("all")}
                data-testid="floor-chip-all"
                className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${activeFloor === "all" ? "border-[#0F2942] bg-[#0F2942] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
              >All</button>
              {currentTower.floors.map((fl) => (
                <button
                  key={fl.floor}
                  onClick={() => setActiveFloor(String(fl.floor))}
                  data-testid={`floor-chip-${fl.floor}`}
                  className={`rounded-lg border px-3 py-1.5 text-sm font-mono transition-colors ${String(activeFloor) === String(fl.floor) ? "border-[#0F2942] bg-[#0F2942] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                >{fl.floor}</button>
              ))}
            </div>
          )}

          {/* Floor matrix — only selected floor (or all) */}
          {currentTower && (
            <motion.div key={activeTower + activeFloor} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="space-y-4">
              {(() => {
                const shown = activeFloor === "all"
                  ? filteredFloors
                  : filteredFloors.filter((fl) => String(fl.floor) === String(activeFloor));
                if (shown.length === 0) {
                  return <p className="text-sm text-slate-400 py-10 text-center">No units match your filters in Tower {activeTower}{activeFloor !== "all" ? `, Floor ${activeFloor}` : ""}.</p>;
                }
                return shown.map((fl) => (
                  <div key={fl.floor} className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={`floor-section-${fl.floor}`}>
                    <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100">
                      <span className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center font-mono text-sm font-semibold text-slate-700">{fl.floor}</span>
                      <span className="font-display font-semibold text-slate-900">Tower {activeTower} · Floor {fl.floor}</span>
                      <span className="text-xs font-mono text-slate-400">{fl.units.length} units</span>
                    </div>
                    <div className="p-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                      {fl.units.map((u) => (
                        <FlatCard key={u.id} flat={u} onClick={() => openFlat(u)} />
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </motion.div>
          )}
        </>
      )}

      <FlatDrawer
        flatId={drawerFlat}
        open={!!drawerFlat}
        onOpenChange={(v) => !v && setDrawerFlat(null)}
        onChanged={load}
        onEdit={onEditFromDrawer}
      />
      <AddFlatDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        editFlat={editFlat}
        onSaved={load}
      />
    </div>
  );
}
